import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useMe } from "../hooks/useMe";
import { getStoredAuthTokens, getUser } from "../utils/auth";

const normalizeRole = (value) => {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
};

const hasStaffRole = (value) => {
  const role = normalizeRole(value);
  return role === "staff" || role === "admin";
};

const isPrivilegedUser = (user) => {
  if (!user) return false;
  if (hasStaffRole(user.role)) return true;
  if (Array.isArray(user.roles) && user.roles.some(hasStaffRole)) return true;
  if (Array.isArray(user.user?.roles) && user.user.roles.some(hasStaffRole)) return true;
  return Boolean(
    user.is_superuser ||
      user.is_staff ||
      user.user?.is_superuser ||
      user.user?.is_staff
  );
};

export default function RequireStaff() {
  const { isLoading, me, refetch } = useMe();
  const location = useLocation();
  const retryingRef = useRef(false);
  const storedUser = getUser();
  const { accessToken, refreshToken } = getStoredAuthTokens();
  const hasAuthToken = Boolean(accessToken || refreshToken);
  const user = me || storedUser;

  const shouldRetryMe = !isLoading && !me && hasAuthToken;
  useEffect(() => {
    if (shouldRetryMe && !retryingRef.current) {
      retryingRef.current = true;
      refetch();
    }
  }, [refetch, shouldRetryMe]);

  useEffect(() => {
    if (me) retryingRef.current = false;
  }, [me]);

  if (isLoading) return null;

  if (!user) {
    if (!hasAuthToken) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname }}
        />
      );
    }
    return null;
  }

  if (!isPrivilegedUser(user)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
